/*
 * Module for viewing and editing a task assigned to a specific topic
 */

define([
  "marionette",
  "messaging/messenger",
  "./tasksAreaView",
  "modules/tasks/tasksModel",
  "jquery",
  "underscore",
  "modules/tasks/taskWizardModal/taskWizardModalModule",
  "./taskStep/taskStepModule",
  "modules/tasks/taskUploads/taskUploadsModule",
  "./taskSchedule/taskScheduleModule",
], function (
  Marionette,
  Messenger,
  TasksAreaView,
  TaskModel,
  $,
  _,
  TaskWizardModalModule,
  TaskStepModule,
  TaskUploadsModule,
  TaskScheduleModule,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.allowDelete = !!options.allowDelete;
      this.name = "tasksArea";
      this.region = options.region;
      this._setUpVents();
      this._setUpReqres();
    },
    show: function (task) {
      this.task = task; // saving this for the reqres that returns the task itself
      const languages = Messenger.reqres.request("kb:languages");
      const touchpoints = Messenger.reqres.request("kb:touchpoints");
      task.allowDelete = this.allowDelete;
      task.languages = languages;
      task.touchpoints = touchpoints;
      task.statusChanged = false;
      this.view = new TasksAreaView.tasksAreaView({ model: task });
      this.view.on(
        "reject:task",
        function () {
          const callbacks = {};
          callbacks.confirm = function (comment) {
            //update the status of the task
            task.statusChanged = true;
            task.set("status", TaskModel.STATUS.FAILED);
            task.addComment(comment);
            this.region.reset();
          };
          callbacks.cancel = _.noop;
          const message =
            task.get("type") === TaskModel.TYPE.TOPIC_REVIEW
              ? $.t("task.topicReview.rejectWithComment")
              : $.t("task.standard.rejectWithComment");
          Messenger.confirmDialog(message, callbacks, this, "task.status.reject", true);
        },
        this,
      );
      this.view.on(
        "start:progress:on:task",
        function () {
          task.statusChanged = true;
          task.set("status", TaskModel.STATUS.IN_PROGRESS);
          this.region.reset();
        },
        this,
      );
      this.view.on(
        "approve:task",
        function () {
          this._confirmApproveOrComplete(task);
        },
        this,
      );
      this.view.on(
        "complete:task",
        function () {
          this._confirmApproveOrComplete(task);
        },
        this,
      );
      this.view.on(
        "ignore:task",
        function () {
          task.statusChanged = true;
          task.set("status", TaskModel.STATUS.IGNORED);
          this.region.reset();
        },
        this,
      );
      this.view.on(
        "resubmit:task",
        function () {
          task.statusChanged = true;
          task.set("status", TaskModel.STATUS.RESUBMITTED);
          //create a copy of it to display.  Can't use clone because that only clones the model, and we need to
          //also clone the steps and assignees if present.
          const newTask = new TaskModel.task(task.toJSON({ parse: true }), { parse: true });
          newTask.set("status", TaskModel.STATUS.NOT_STARTED);
          newTask.unset("taskId");
          newTask.unset("id");
          if (newTask.get("type") == TaskModel.TYPE.ADVANCED) {
            newTask.steps().each(function (taskStep) {
              taskStep.set("status", TaskModel.STATUS.CREATED);
              taskStep.unset("id");
              taskStep.unset("stepId");
              taskStep.unset("taskId");
              taskStep.assignees().each(function (taskStepAssignee) {
                taskStepAssignee.unset("id");
                taskStepAssignee.unset("taskStepId");
              });
            });
          }
          //add it to the list
          Messenger.vent.trigger("show:resubmitted:task", newTask);
          //close the region
          this.region.reset();
        },
        this,
      );
      this.view.on(
        "delete:task",
        function () {
          Messenger.yesNoDialog("task.confirmDelete", {
            yes: function () {
              Messenger.vent.trigger("track:interaction", {
                action: "Delete Task",
                id: task.id,
                idType: "Task",
                object: "Task",
                source: "Task",
              });
              //deselect it prior to deleting it
              Messenger.vent.trigger("deselect:task");
              task.deleteModel();
              Messenger.vent.trigger("show:no:task");
            },
          });
        },
        this,
      );
      this.view.on(
        "change:task:language",
        function (languageId) {
          task.set("languageId", languageId);
        },
        this,
      );
      this.view.on(
        "change:task:targetLanguage",
        function (targetLanguageId) {
          task.set("targetLanguageId", targetLanguageId);
        },
        this,
      );
      this.view.on(
        "change:task:touchpoint",
        function (touchpointId) {
          task.set("touchpointId", touchpointId);
        },
        this,
      );
      this.view.on(
        "change:task:user:unassigned",
        function () {
          task.set("user", "");
        },
        this,
      );
      this.view.on(
        "change:task:dueDate",
        function (dueDate) {
          task.set("dueDate", dueDate);
        },
        this,
      );
      this.view.on("edit:task", this._onEditTask, this);
      this.view.on("add:comment", this._onAddComment, this);
      this.view.on("show", this._showSubmodules, this);
      if (task.get("type") == TaskModel.TYPE.ADVANCED) {
        this.stepsModule = new TaskStepModule({ region: this.view.steps });
        this.uploadsModule = new TaskUploadsModule({
          region: this.view.uploads,
          allowAdd: Messenger.reqres.request("can:create:task"),
          allowDelete: Messenger.reqres.request("can:create:task"),
          allowUpdate: Messenger.reqres.request("can:create:task"),
        });
      } else {
        this.scheduleModule = new TaskScheduleModule({
          region: this.view.schedule,
        });
      }
      this.showRegion(this.view);
    },
    _showSubmodules: function () {
      if (this.task.get("type") == TaskModel.TYPE.ADVANCED) {
        this.stepsModule.show(this.task.steps());
        this.uploadsModule.show(this.task, this.task.uploads());
      } else {
        this.scheduleModule.show(this.task);
      }
    },
    _setUpVents: function () {
      //show the region when a task is selected
      Messenger.vent.on(
        "selected:task",
        function (task) {
          this.show(task);
        },
        this,
      );
      Messenger.vent.on("deselect:task", function () {
        if (this.view) {
          this.view.close();
        }
        this.task = null;
      });
    },
    _setUpReqres: function () {
      Messenger.reqres.setHandler(
        "get:selected:task",
        function () {
          return this.task;
        },
        this,
      );
    },
    _confirmApproveOrComplete: function (task) {
      const callbacks = {};
      const _this = this;
      callbacks.confirm = function (comment) {
        //update the status of the task
        task.statusChanged = true;
        task.set("status", TaskModel.STATUS.COMPLETED);
        task.addComment(comment);
        _this.region.reset();
      };
      const message =
        task.get("type") === TaskModel.TYPE.TOPIC_REVIEW
          ? $.t("task.topicReview.acceptWithComment")
          : $.t("task.standard.acceptWithComment");
      const buttonLabel =
        task.get("type") === TaskModel.TYPE.TOPIC_REVIEW
          ? "task.status.approve"
          : "task.status.complete";
      Messenger.confirmDialog(message, callbacks, this, buttonLabel, true);
    },
    _onAddComment: function () {
      const callbacks = {};
      const _this = this;
      callbacks.confirm = function (comment) {
        _this.task.addComment(comment);
        _this.region.reset();
      };
      Messenger.confirmDialog($.t("task.addComment"), callbacks, this, "common.add", true);
    },
    _onEditTask: function () {
      if (!this.task) {
        return;
      }
      Messenger.vent.trigger("show:modal", TaskWizardModalModule, {}, this.task, {
        closeButton: false,
        closeOnBackgroundClick: false,
        title: "task.editTask",
        trackInteraction: {
          action: "Edit Advanced Task",
          id: this.task.id,
          idType: "Task",
          object: "Task",
        },
      });
    },
  });
});
